// src/contexts/AuthContext.js
// ✅ BUG-002 FIX: Added session management with 24h absolute + 1h idle timeout
// ✅ GAP-SEC-008 FIX (Mar 21, 2026): Multi-Tenant Isolation
// ✅ GAP-SEC-001 FIX (Mar 22, 2026): Audit Logging — login, logout, session expiry, force logout
// ✅ USER-007 FIX (Apr 5, 2026): Token refresh on role change via Firestore tokenVersion listener
// ✅ TRACK-C (Apr 6, 2026): GAP-FIRM-002 — License expiry block on login
// ✅ UI-FIX (Mar 28, 2026): getSubdomain() — replaced inline copy with import of
//    src/utils/getSubdomain.js. Old inline version returned "firm1" for firm3.localhost.
//    Now correctly resolves any *.localhost subdomain (firm1..firm4, etc.) and production.
// ✅ BUG-AUTH-QUOTA-001 FIX (Jun 5, 2026): auth/quota-exceeded on production domains
//
// ROOT CAUSE:
//   The main onAuthStateChanged useEffect had a dependency array:
//   [getSessionData, saveSessionData, clearSessionData, logout, getSubdomain, checkLicense]
//   Each of these is a useCallback. When any of their own dependencies changed (e.g.
//   currentUser, firmId, userRole), they got new references → the dependency array
//   changed → useEffect tore down the listener and re-subscribed → onAuthStateChanged
//   fired again → triggered checkLicense + state updates → re-render → loop.
//   On localhost this was invisible (fast local network). On production domains
//   (demo.handyy.in), real network latency made each loop iteration slower and wider,
//   flooding Firebase Auth with hundreds of token requests per minute → quota-exceeded.
//
// FIX:
//   1. Main useEffect dependency array changed to [] — listener subscribes ONCE on mount.
//   2. All helper functions captured via stable refs (useRef) so the listener always
//      calls the latest version without needing to re-subscribe.
//   3. Ref sync effects added for each helper to keep refs current on every render.
//
// SUPPORT NOTE (for CS/Ops team):
//   If a newly onboarded firm reports "stuck on Loading..." or auth/quota-exceeded:
//   - This fix eliminates the root cause permanently.
//   - If seen on an older build: redeploy with this fix and ask user to hard-refresh.
//   - See: BUG-AUTH-QUOTA-001 in support runbook.
//
// WHAT CHANGED (TRACK-C GAP-FIRM-002):
//   1. After firm is validated in onAuthStateChanged, fetch the license document.
//   2. Check licenseStatus and expiryDate — if expired/suspended → force logout.
//   3. licenseError state added — exported for Login UI to display reason.
//   4. licenseInfo state added — exported so LicenseManagement.jsx can display plan.
//   5. New helper: checkLicense(firmId) — returns {ok, reason, license} object.
//   6. Added "license_expired" and "license_suspended" as logout reasons.
//
// NOTE ON LOCALHOST DEV:
//   License check runs in all environments. Ensure firms/{firmId}/licenses/license_{firmId}
//   exists in your dev Firestore (run: node scripts/seed-dev-licenses.js).
//   If no license doc exists, the check is skipped with a console warning — dev unblocked.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useContext, useEffect, useState, createContext, useCallback, useRef } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  getIdTokenResult,
} from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import auditService, { AUDIT_ACTIONS } from "../services/AuditService"; // ✅ GAP-SEC-001
import { getSubdomain as getSubdomainUtil } from "../utils/getSubdomain"; // ✅ UI-FIX Mar 28: single source of truth

// BUG-AUDIT-002 FIX (Mar 27, 2026): Module-level login audit guard.
let _auditedLoginUid = null;

const AuthContext = createContext();

// ✅ BUG-002 FIX: Session configuration constants
const SESSION_CONFIG = {
  ABSOLUTE_TIMEOUT: 24 * 60 * 60 * 1000,
  IDLE_TIMEOUT: 60 * 60 * 1000,
  WARNING_BEFORE_LOGOUT: 5 * 60 * 1000,
  ACTIVITY_CHECK_INTERVAL: 30 * 1000,
  STORAGE_KEY: "handyy_session",
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState(null);
  const [firmId, setFirmId] = useState(null);
  const [professionalDetails, setProfessionalDetails] = useState(null);
  const [personalDetails, setPersonalDetails] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState("checking");
  const [firmValidationError, setFirmValidationError] = useState(null);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [timeUntilLogout, setTimeUntilLogout] = useState(null);
  const [logoutReason, setLogoutReason] = useState(null);

  // ✅ TRACK-C: License state
  const [licenseError, setLicenseError] = useState(null);    // string | null — shown on login screen
  const [licenseInfo, setLicenseInfo] = useState(null);      // license doc data | null — for LicenseManagement.jsx

  // USER-007 fix: role change notification
  const [roleChangedNotification, setRoleChangedNotification] = useState(null);

  const activityTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const tokenVersionListenerRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────
  // ✅ BUG-AUTH-QUOTA-001 FIX: Stable refs for all helper functions
  //
  // These refs let the main onAuthStateChanged useEffect (dep array: [])
  // always call the LATEST version of each helper without needing to
  // re-subscribe. This breaks the re-render → re-subscribe loop that
  // caused auth/quota-exceeded on production domains.
  // ─────────────────────────────────────────────────────────────────────
  const checkLicenseRef    = useRef(null);
  const saveSessionRef     = useRef(null);
  const getSessionRef      = useRef(null);
  const clearSessionRef    = useRef(null);
  const logoutRef          = useRef(null);
  const getSubdomainRef    = useRef(null);

  // ✅ BUG-002 FIX: Session storage helpers
  const getSessionData = useCallback(() => {
    try {
      const data = localStorage.getItem(SESSION_CONFIG.STORAGE_KEY);
      if (!data) return null;
      const parsed = JSON.parse(data);
      if (parsed && parsed.loginTime && parsed.lastActivity) return parsed;
      return null;
    } catch (error) {
      console.error("Error reading session data:", error);
      return null;
    }
  }, []);

  const saveSessionData = useCallback((data) => {
    try {
      localStorage.setItem(
        SESSION_CONFIG.STORAGE_KEY,
        JSON.stringify({ ...data, lastActivity: Date.now() })
      );
    } catch (error) {
      console.error("Failed to save session data:", error);
    }
  }, []);

  const clearSessionData = useCallback(() => {
    try {
      localStorage.removeItem(SESSION_CONFIG.STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear session data:", error);
    }
  }, []);

  const updateActivity = useCallback(() => {
    const sessionData = getSessionData();
    if (sessionData) {
      saveSessionData(sessionData);
    }
  }, [getSessionData, saveSessionData]);

  // ─────────────────────────────────────────────────────────────────────
  // ✅ UI-FIX (Mar 28, 2026): getSubdomain — delegates to shared utility
  //
  // PREVIOUS BUG: Inline copy only handled bare "localhost"/"127.0.0.1".
  //   firm3.localhost:3000 → host has 2 parts → fell through to "firm1"
  //   fallback → firmId mismatch with token claim "firm3" → login blocked.
  //
  // FIX: Delegates to src/utils/getSubdomain.js — single source of truth.
  //   Works correctly for ALL environments:
  //   ┌──────────────────────┬──────────────────────────────┐
  //   │ Host                 │ Returns                      │
  //   ├──────────────────────┼──────────────────────────────┤
  //   │ localhost            │ "firm1" (env var / fallback) │
  //   │ 127.0.0.1            │ "firm1" (env var / fallback) │
  //   │ firm1.localhost      │ "firm1" ✅                   │
  //   │ firm2.localhost      │ "firm2" ✅                   │
  //   │ firm3.localhost      │ "firm3" ✅ (was "firm1"bug)  │
  //   │ firm4.localhost      │ "firm4" ✅                   │
  //   │ firm1.handyy.in      │ "firm1" (production) ✅      │
  //   │ firm2.handyy.in      │ "firm2" (production) ✅      │
  //   │ handyy.in            │ null (prod) / "firm1" (dev)  │
  //   └──────────────────────┴──────────────────────────────┘
  // ─────────────────────────────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getSubdomain = useCallback(() => getSubdomainUtil(), []);

  // ─────────────────────────────────────────────────────────────────────
  // ✅ TRACK-C: checkLicense()
  //
  // Fetches firms/{firmId}/licenses/license_{firmId} and validates:
  //   - licenseStatus must be 'active'
  //   - expiryDate must be in the future
  //
  // Returns: { ok: boolean, reason: string|null, license: object|null }
  //
  // SAFE DESIGN: If the license doc doesn't exist (dev environment without
  // seed data) → returns ok=true with a console warning. This prevents
  // locking out developers who haven't run seed-dev-licenses.js.
  // ─────────────────────────────────────────────────────────────────────
  const checkLicense = useCallback(async (fId) => {
    try {
      const licenseRef = doc(db, "firms", fId, "licenses", `license_${fId}`);
      const licenseSnap = await getDoc(licenseRef);

      if (!licenseSnap.exists()) {
        console.warn("⚠️ GAP-FIRM-002: No license doc found for firm:", fId,
          "— skipping license check. Run seed-dev-licenses.js to create one.");
        return { ok: true, reason: null, license: null };
      }

      const license = licenseSnap.data();
      console.log("📋 License fetched:", JSON.stringify({
        plan: license.plan,
        status: license.licenseStatus,
        expiryDate: license.expiryDate?.toDate?.()?.toISOString?.() || license.expiryDate,
      }));

      // Check suspended/inactive
      if (license.licenseStatus && license.licenseStatus !== "active") {
        return {
          ok: false,
          reason: `Your firm's license is ${license.licenseStatus}. Please contact support@lexcorelawtech.in to resolve your account status.`,
          license,
        };
      }

      // Check expiry
      if (license.expiryDate) {
        const expiryMs = license.expiryDate?.toMillis
          ? license.expiryDate.toMillis()
          : new Date(license.expiryDate).getTime();

        if (expiryMs < Date.now()) {
          const expiryDateStr = new Date(expiryMs).toLocaleDateString("en-IN");
          return {
            ok: false,
            reason: `Your firm's license expired on ${expiryDateStr}. Please contact support@lexcorelawtech.in to renew.`,
            license,
          };
        }
      }

      return { ok: true, reason: null, license };
    } catch (err) {
      // Non-fatal — don't block login on a transient Firestore error
      console.error("⚠️ License check failed with unexpected error (non-blocking):", err.message);
      return { ok: true, reason: null, license: null };
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // logout()
  // ─────────────────────────────────────────────────────────────────────
  const logout = useCallback(async (reason = "manual") => {
    console.log("🚪 Logging out, reason:", reason);
    setLogoutReason(reason);

    if (tokenVersionListenerRef.current) {
      tokenVersionListenerRef.current();
      tokenVersionListenerRef.current = null;
    }

    if (activityTimerRef.current) {
      clearInterval(activityTimerRef.current);
      activityTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    const logReason = reason === "idle_timeout"
      ? AUDIT_ACTIONS.AUTH_SESSION_EXPIRED
      : reason === "force_logout"
      ? AUDIT_ACTIONS.AUTH_FORCE_LOGOUT
      : AUDIT_ACTIONS.AUTH_LOGOUT;

    try {
      if (currentUser && firmId) {
        await auditService.logAuth(
          firmId,
          logReason,
          currentUser.uid,
          userName || "Unknown",
          userRole || "Unknown"
        );
      }
    } catch (auditErr) {
      console.warn("[AuthContext] Audit logout log failed (non-blocking):", auditErr);
    }

    clearSessionData();
    _auditedLoginUid = null;

    await signOut(auth);
  }, [currentUser, firmId, userName, userRole, clearSessionData]);

  const clearRoleChangedNotification = useCallback(() => {
    setRoleChangedNotification(null);
  }, []);

  // ✅ USER-007 FIX: forceRefreshToken()
  const forceRefreshToken = useCallback(async () => {
    if (!currentUser) return;
    try {
      console.log("[USER-007] Force refreshing ID token...");
      const tokenResult = await getIdTokenResult(currentUser, true);
      const newRole = tokenResult.claims.role || null;
      if (newRole) {
        setUserRole(normalizeRole(newRole));
        setRoleChangedNotification(newRole);
        console.log("[USER-007] Token refreshed — new role:", newRole);
      }
    } catch (err) {
      console.error("[USER-007] Force token refresh failed:", err.message);
    }
  }, [currentUser]);

  // ─────────────────────────────────────────────────────────────────────
  // Session check
  // ─────────────────────────────────────────────────────────────────────
  const checkSession = useCallback(() => {
    if (!currentUser) return;
    const sessionData = getSessionData();
    if (!sessionData) {
      logout("idle_timeout");
      return;
    }
    const now = Date.now();
    const timeSinceLogin = now - sessionData.loginTime;
    const timeSinceActivity = now - sessionData.lastActivity;

    if (timeSinceLogin >= SESSION_CONFIG.ABSOLUTE_TIMEOUT) {
      logout("session_expired");
      return;
    }
    if (timeSinceActivity >= SESSION_CONFIG.IDLE_TIMEOUT) {
      logout("idle_timeout");
      return;
    }

    const timeUntilIdleExpiry = SESSION_CONFIG.IDLE_TIMEOUT - timeSinceActivity;
    const timeUntilAbsoluteExpiry = SESSION_CONFIG.ABSOLUTE_TIMEOUT - timeSinceLogin;
    const timeUntilExpiry = Math.min(timeUntilIdleExpiry, timeUntilAbsoluteExpiry);

    if (timeUntilExpiry <= SESSION_CONFIG.WARNING_BEFORE_LOGOUT && !sessionWarning) {
      console.log("⚠️ Session expiring soon, showing warning");
      setSessionWarning(true);
      setTimeUntilLogout(Math.ceil(timeUntilExpiry / 1000));

      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);

      countdownTimerRef.current = setInterval(() => {
        setTimeUntilLogout((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  }, [currentUser, getSessionData, logout, sessionWarning]);

  const extendSession = useCallback(() => {
    console.log("✅ Session extended by user action");
    updateActivity();
    setSessionWarning(false);
    setTimeUntilLogout(null);
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, [updateActivity]);

  // ✅ BUG-002 FIX: Setup activity listeners
  useEffect(() => {
    if (!currentUser || authStatus !== "authorized") return;

    console.log("🔧 Setting up session activity tracking");

    const activityEvents = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    let activityTimeout = null;
    const handleActivity = () => {
      if (activityTimeout) return;
      activityTimeout = setTimeout(() => {
        updateActivity();
        activityTimeout = null;
      }, 1000);
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    activityTimerRef.current = setInterval(checkSession, SESSION_CONFIG.ACTIVITY_CHECK_INTERVAL);
    checkSession();

    return () => {
      console.log("🧹 Cleaning up session activity tracking");
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (activityTimerRef.current) {
        clearInterval(activityTimerRef.current);
        activityTimerRef.current = null;
      }
      if (activityTimeout) clearTimeout(activityTimeout);
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [currentUser, authStatus, updateActivity, checkSession]);

  // ─────────────────────────────────────────────────────────────────────
  // ✅ USER-007 FIX: tokenVersion Firestore listener
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentUser || !firmId || authStatus !== "authorized") return;

    const userDocRef = doc(db, "firms", firmId, "users", currentUser.uid);
    let previousTokenVersion = null;
    let isFirstSnapshot = true;

    const unsubscribe = onSnapshot(
      userDocRef,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        const currentTokenVersion = data.tokenVersion;

        if (isFirstSnapshot) {
          isFirstSnapshot = false;
          previousTokenVersion = currentTokenVersion;
          return;
        }

        if (currentTokenVersion !== previousTokenVersion) {
          previousTokenVersion = currentTokenVersion;
          console.log("[USER-007] tokenVersion changed — forcing token refresh");
          forceRefreshToken();
        }
      },
      (err) => {
        console.warn("[AuthContext] tokenVersion listener error (non-fatal):", err.message);
      }
    );

    tokenVersionListenerRef.current = unsubscribe;

    return () => {
      console.log("🧹 [USER-007] Cleaning up tokenVersion listener");
      unsubscribe();
      tokenVersionListenerRef.current = null;
    };
  }, [currentUser, firmId, authStatus, forceRefreshToken]);

  // Normalize role names
  const normalizeRole = (role) => {
    if (!role) return null;
    const roleMap = {
      SeniorAssociate: "SrAssociate",
      "Senior Associate": "SrAssociate",
      ManagingPartner: "ManagingPartner",
      "Managing Partner": "ManagingPartner",
      Reciptionist: "Receptionist",
      Receptionist: "Receptionist",
      Partner: "Partner",
      Associate: "Associate",
      Intern: "Intern",
      Admin: "Admin",
      Accountant: "Accountant",
    };
    return roleMap[role] || role;
  };

  // Role → Default route mapping
  const roleToRoute = (role) => {
    if (!role) return "/dashboard";
    switch (role) {
      case "Admin":
      case "ManagingPartner":
        return "/admin/dashboard";
      case "Partner":
        return "/partner/dashboard";
      case "SrAssociate":
        return "/admin/dashboard";
      case "Associate":
        return "/associate/dashboard";
      case "Intern":
        return "/intern/dashboard";
      case "Receptionist":
        return "/receptionist/dashboard";
      case "Accountant":
        return "/accounts";
      default:
        return "/unauthorized";
    }
  };


  // ─────────────────────────────────────────────────────────────────────
  // ✅ BUG-AUTH-QUOTA-001 FIX: Keep stable refs current on every render
  // This is the standard pattern for using refs to break useEffect loops.
  // Each useEffect here has a SINGLE dependency — the function itself —
  // so it only updates the ref when that function actually changes.
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => { checkLicenseRef.current  = checkLicense;   }, [checkLicense]);
  useEffect(() => { saveSessionRef.current   = saveSessionData; }, [saveSessionData]);
  useEffect(() => { getSessionRef.current    = getSessionData;  }, [getSessionData]);
  useEffect(() => { clearSessionRef.current  = clearSessionData;}, [clearSessionData]);
  useEffect(() => { logoutRef.current        = logout;          }, [logout]);
  useEffect(() => { getSubdomainRef.current  = getSubdomain;    }, [getSubdomain]);
  // ─────────────────────────────────────────────────────────────────────
  // Main auth state listener
  // ✅ TRACK-C: License check added after firm validation.
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // ✅ BUG-AUTH-QUOTA-001 FIX: dep array is [] — listener subscribes ONCE on mount only.
    // All helpers accessed via stable refs (checkLicenseRef, etc.) so we always call
    // the latest version without re-subscribing. This eliminates the re-render loop
    // that flooded Firebase Auth with requests on production domains.
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log("🔄 Auth state changed:", firebaseUser ? "User logged in" : "User logged out");

      try {
        if (firebaseUser) {
          setCurrentUser(firebaseUser);
          setAuthStatus("checking");

          // ✅ BUG-AUTH-QUOTA-001: use ref — not the useCallback directly
          const subdomain = getSubdomainRef.current();
          console.log("🌐 Subdomain detected:", subdomain);

          if (!subdomain) {
            console.error("🚫 GAP-SEC-008: No valid subdomain — blocking auth");
            setFirmValidationError(
              "Invalid firm URL. Please navigate to your firm's URL (e.g. yourfirm.handyy.in)."
            );
            setAuthStatus("unauthorized");
            setCurrentUser(null);
            setLoading(false);
            await signOut(auth);
            return;
          }

          setFirmId(subdomain);

          // ✅ TRACK-C — GAP-FIRM-002: License check BEFORE any session setup
          // ✅ BUG-AUTH-QUOTA-001: use ref
          const licenseResult = await checkLicenseRef.current(subdomain);
          if (!licenseResult.ok) {
            console.error("🚫 GAP-FIRM-002: License check failed:", licenseResult.reason);
            setLicenseError(licenseResult.reason);
            setLicenseInfo(licenseResult.license);
            setAuthStatus("unauthorized");
            setCurrentUser(null);
            setLoading(false);
            await signOut(auth);
            return;
          }

          setLicenseInfo(licenseResult.license);
          setLicenseError(null);

          // ✅ BUG-002 FIX: Check for existing session on page load
          // ✅ BUG-AUTH-QUOTA-001: use refs
          const existingSession = getSessionRef.current();

          if (existingSession) {
            const now = Date.now();
            const timeSinceLogin = now - existingSession.loginTime;
            const timeSinceActivity = now - existingSession.lastActivity;

            if (timeSinceLogin >= SESSION_CONFIG.ABSOLUTE_TIMEOUT) {
              console.log("⏰ Existing session expired (absolute timeout) on page load");
              await logoutRef.current("session_expired_on_load");
              return;
            }

            if (timeSinceActivity >= SESSION_CONFIG.IDLE_TIMEOUT) {
              console.log("⏰ Existing session expired (idle timeout) on page load");
              await logoutRef.current("session_expired_on_load");
              return;
            }

            console.log("✅ Existing session is valid, continuing...");
            saveSessionRef.current({ ...existingSession, lastActivity: Date.now() });
          } else {
            console.log("📝 Creating new session");
            saveSessionRef.current({
              loginTime: Date.now(),
              lastActivity: Date.now(),
              userId: firebaseUser.uid,
              firmId: subdomain,
            });
          }

          // Get token claims (force refresh = true to get latest custom claims)
          const token = await getIdTokenResult(firebaseUser, true);
          console.log("🔑 Token claims:", token.claims);

          // ✅ GAP-SEC-008: Cross-check token.firmId vs URL subdomain
          const tokenFirmId = token.claims.firmId || null;

          if (tokenFirmId && subdomain && tokenFirmId !== subdomain) {
            console.error("🚨 GAP-SEC-008 SECURITY: firmId mismatch detected", {
              urlSubdomain: subdomain,
              tokenFirmId,
            });
            setFirmValidationError(
              `Firm access mismatch. Your account belongs to "${tokenFirmId}". Please use your firm's correct URL.`
            );
            await logoutRef.current("firm_mismatch");
            return;
          }

          if (!tokenFirmId) {
            console.warn("⚠️ GAP-SEC-008: token has no firmId claim.", {
              uid: firebaseUser.uid,
              subdomain,
            });
          }

          // Get user doc from Firestore
          const userDocRef = doc(db, "firms", subdomain, "users", firebaseUser.uid);
          console.log("📄 Fetching user doc from:", `firms/${subdomain}/users/${firebaseUser.uid}`);

          const userSnap = await getDoc(userDocRef);

          if (userSnap.exists()) {
            const userData = userSnap.data();
            console.log("✅ UserDoc found:", userData);

            const normalizedRole = normalizeRole(userData.role);
            const resolvedName = userData.name || "User";
            console.log("🔑 Role normalized:", userData.role, "→", normalizedRole);

            setUserRole(normalizedRole);
            setUserName(resolvedName);
            setProfessionalDetails(userData.professionalDetails || null);
            setPersonalDetails(userData.personalDetails || null);
            setPhotoUrl(userData.photoUrl || null);
            setFirmValidationError(null);
            setAuthStatus("authorized");

            console.log("✅ User authorized:", {
              uid: firebaseUser.uid,
              role: normalizedRole,
              name: resolvedName,
              firmId: subdomain,
            });

            // BUG-AUDIT-002 FIX: Module-level guard prevents duplicate AUTH_LOGIN
            if (_auditedLoginUid !== firebaseUser.uid) {
              _auditedLoginUid = firebaseUser.uid;
              auditService.logAuth(
                subdomain,
                AUDIT_ACTIONS.AUTH_LOGIN,
                firebaseUser.uid,
                resolvedName,
                normalizedRole
              ).catch((err) =>
                console.warn("[AuthContext] Audit login log failed (non-blocking):", err)
              );
            } else {
              console.log("[AuthContext] Skipping duplicate AUTH_LOGIN audit (StrictMode/token-refresh)");
            }

          } else {
            console.warn("⚠️ No user record found in Firestore for UID:", firebaseUser.uid);
            setUserRole(null);
            setUserName(null);
            setProfessionalDetails(null);
            setPersonalDetails(null);
            setPhotoUrl(null);
            setAuthStatus("unauthorized");
            // ✅ BUG-AUTH-QUOTA-001: use ref
            clearSessionRef.current();
          }
        } else {
          // Not signed in
          console.log("🚪 User signed out");

          // ✅ FIX KAL-005 (Jun 24, 2026): Client portal bypass in AuthContext.
          // /client-portal uses JWT magic-link tokens — clients have no Firebase Auth account.
          // AuthContext must not treat this as a sign-out event and redirect to /login.
          // The JWT token is validated independently by ClientPortalDashboard → getClientData CF.
          // Safe: we simply set loading=false and authStatus="login" without clearing state
          // or redirecting — AppRoutes renders ClientPortalDashboard directly for this path.
          if (window.location.pathname === '/client-portal') {
            console.log("🔓 Client portal path detected — skipping auth redirect");
            setAuthStatus("portal");  // ✅ Move out of "checking" so AppRoutes unblocks
            setLoading(false);
            return;
          }

          if (tokenVersionListenerRef.current) {
            console.log("🧹 [USER-007] Tearing down tokenVersion listener on sign-out");
            tokenVersionListenerRef.current();
            tokenVersionListenerRef.current = null;
          }

          setCurrentUser(null);
          setUserRole(null);
          setUserName(null);
          setFirmId(null);
          setProfessionalDetails(null);
          setPersonalDetails(null);
          setPhotoUrl(null);
          setAuthStatus("login");
          // ✅ BUG-AUTH-QUOTA-001: use ref
          clearSessionRef.current();

          setSessionWarning(false);
          setTimeUntilLogout(null);
        }
      } catch (err) {
        console.error("🔥 AuthContext error:", err);
        setCurrentUser(null);
        setUserRole(null);
        setUserName(null);
        setFirmId(null);
        setProfessionalDetails(null);
        setPersonalDetails(null);
        setPhotoUrl(null);
        setAuthStatus("error");
        // ✅ BUG-AUTH-QUOTA-001: use ref
        clearSessionRef.current();
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []); // ✅ BUG-AUTH-QUOTA-001 FIX: Empty dep array — subscribe ONCE on mount only.

  // ─────────────────────────────────────────────────────────────────────
  // ✅ GAP-SEC-008 FIX: Hardened login()
  // ─────────────────────────────────────────────────────────────────────
  const login = async (email, password) => {
    try {
      console.log("🔑 Starting login process...");

      const subdomain = getSubdomain();
      if (!subdomain) {
        throw Object.assign(
          new Error("Invalid firm URL. Please navigate to your firm's URL (e.g. yourfirm.handyy.in)."),
          { code: "auth/invalid-firm-url" }
        );
      }

      // Verify firm exists in Firestore
      console.log("🏢 Validating firm:", subdomain);
      const firmRef = doc(db, "firms", subdomain);
      const firmSnap = await getDoc(firmRef);

      if (!firmSnap.exists()) {
        console.error("🚫 GAP-SEC-008: Firm not found in Firestore:", subdomain);
        throw Object.assign(
          new Error(`Firm "${subdomain}" not found. Please check your URL.`),
          { code: "auth/firm-not-found" }
        );
      }

      console.log("✅ Firm validated:", subdomain);

      clearSessionData();

      const result = await signInWithEmailAndPassword(auth, email, password);
      console.log("🔑 Login successful:", result.user.uid);

      saveSessionData({
        loginTime: Date.now(),
        lastActivity: Date.now(),
        userId: result.user.uid,
        firmId: subdomain,
      });

      setFirmValidationError(null);
      return result;
    } catch (error) {
      console.error("🔥 Login error:", error);
      throw error;
    }
  };

  const value = {
    currentUser,
    userRole,
    userName,
    firmId,
    professionalDetails,
    personalDetails,
    photoUrl,
    login,
    logout,
    loading,
    authStatus,
    roleToRoute,
    // ✅ BUG-002 FIX: Session management exports
    sessionWarning,
    timeUntilLogout,
    logoutReason,
    extendSession,
    updateActivity,
    // ✅ GAP-SEC-008: Firm validation error
    firmValidationError,
    // ✅ USER-007 FIX: Token refresh exports
    forceRefreshToken,
    roleChangedNotification,
    clearRoleChangedNotification,
    // ✅ TRACK-C: License exports
    licenseError,
    licenseInfo,
  };

  console.log("📊 AuthContext state:", {
    currentUser: !!currentUser,
    userRole,
    userName,
    firmId,
    loading,
    authStatus,
    sessionWarning,
    timeUntilLogout,
    firmValidationError,
    licenseError: licenseError ? "SET" : null,
  });

  return (
    <AuthContext.Provider value={value}>
      {loading && authStatus === "checking" ? (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading authentication...</p>
          </div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthProvider;
